import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown } from "components";
import { useAnchor } from "hooks";
import { getImageFormat } from "lib/utils";
import { Features17Content } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Features17Content;
}

const Features17: VFC<Props> = withErrorBoundary(
  ({ content: { features17IconSection, features17Title, features17Description, features17List } }) => {
    useAnchor("features17");

    return (
      <StyledFeatures17 className="features17 Content">
        <div className="Features17__textSection">
          <ul className="Features17__iconList">
            {features17IconSection.map(
              (
                {
                  icon: {
                    data: {
                      icon: { ext, ...icon },
                    },
                  },
                },
                index
              ) => (
                <li key={index}>
                  <LegacyImage
                    {...icon}
                    layout="fill"
                    objectFit="cover"
                    transform={{ format: getImageFormat(ext) }}
                    className="Features17__icon"
                    style={{ zIndex: `${features17IconSection.length - index}` }}
                  />
                </li>
              )
            )}
          </ul>
          <h2 className="Features17__title Font_48_54_600 lg:Font_40_48_600">{features17Title}</h2>
          <Markdown className="Features17__description Color_secondary Font_24_32_450">
            {features17Description}
          </Markdown>
        </div>
        <ul className="Features17__list">
          {features17List.map(({ listTitle, listDescription }) => (
            <li key={listTitle} className="Features17__listItem">
              <h3 className="Features17__listItemTitle Font_24_32_600">{listTitle}</h3>
              <Markdown className="Features17__listItemDescription Color_secondary Font_18_26_450">
                {listDescription}
              </Markdown>
            </li>
          ))}
        </ul>
      </StyledFeatures17>
    );
  },
  {}
);

const StyledFeatures17 = styled.section`
  display: flex;
  justify-content: space-between;
  margin-top: -64px;
  padding-top: 64px;

  .Features17__iconList {
    width: fit-content;
    display: flex;
    padding: 10px;
    border-radius: 100px;
    background-color: rgb(20 20 20 / 8%);
  }

  .Features17__iconList > *:not(:first-child) {
    margin-left: -4px;
  }

  .Features17__icon {
    position: relative;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    overflow: hidden;
  }

  .Features17__title {
    width: 586px;
    margin-top: 40px;
  }

  .Features17__description {
    width: 532px;
    margin-top: 24px;
  }

  .Features17__textSection {
    width: 586px;
    padding-bottom: 11px;
  }

  .Features17__list {
    max-width: 550px;
    margin-left: 80px;
    align-self: end;
  }

  .Features17__listItem:not(:first-child) {
    margin-top: 40px;
  }

  .Features17__listItemDescription {
    margin-top: 8px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    justify-content: flex-start;

    .Features17__textSection {
      width: 464px;
      padding-bottom: 0;
    }

    .Features17__list {
      max-width: 413px;
      margin-left: 83px;
    }

    .Features17__title {
      width: 100%;
    }

    .Features17__description {
      width: 100%;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    display: flex;
    flex-direction: column;

    .Features17__textSection {
      width: 581px;
    }

    .Features17__list {
      max-width: 581px;
      margin-left: 0;
      margin-top: 60px;
      align-self: start;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Features17__textSection {
      max-width: 100%;
    }
  }
`;

gql`
  fragment Features17Content on ComponentFeaturesFeatures17 {
    features17IconSection {
      id
      icon {
        data {
          icon: attributes {
            src: hash
            alt: alternativeText
            ext
          }
        }
      }
    }
    features17Title
    features17Description
    features17List {
      listTitle
      listDescription
    }
  }
`;

export { Features17 };
export type { Props as Features17Props };
